<script setup lang="ts">
  import { ref, computed, watch, type Component } from 'vue'
  import type {
    FieldEntityTarget,
    FieldInputType,
    AddCustomFieldRequestPayload
  } from '@gat-ship/types'
  import { globalUtils } from '@gat-ship/utils'
  import {
    IconTextCursor,
    IconAlignLeft,
    IconHash,
    IconCalendarDays,
    IconSquareCheck,
    IconChevronDown
  } from '@gat-ship/icons'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    InputGroupNumber,
    InputGroupTextarea,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import FieldValidationConfigActionCard from '../FieldValidationConfigActionCard.vue'
  import FieldVisibilityConfigActionCard from '../FieldVisibilityConfigActionCard.vue'
  import FieldEditableConfigActionCard from '../FieldEditableConfigActionCard.vue'

  const props = defineProps<{
    target: FieldEntityTarget
    existingFieldNames: string[]
  }>()

  const emit = defineEmits<{
    (e: 'add', payload: AddCustomFieldRequestPayload): void
  }>()

  const open = defineModel<boolean>('open', { default: false })

  // ── State ─────────────────────────────────────────────────────

  const step = ref<1 | 2>(1)
  const inputType = ref<FieldInputType>('text')

  // ── Name generation ────────────────────────────────────────────

  function generateFieldName(label: string): string {
    let base = label
      .toLowerCase()
      .replace(/[^a-z0-9\s_-]/g, '')
      .replace(/\s+/g, '_')
      .replace(/^_+|_+$/g, '')

    if (!base || !/^[a-z]/.test(base)) base = `field_${base || 'custom'}`

    const taken = new Set(props.existingFieldNames)
    if (!taken.has(base)) return base

    let counter = 2
    while (taken.has(`${base}_${counter}`)) counter++
    return `${base}_${counter}`
  }

  // ── Form ──────────────────────────────────────────────────────

  const addFieldSchema = z
    .object({
      label: z.string().min(1, 'Label is required'),
      name: z
        .string()
        .min(1, 'Name is required')
        .regex(/^[a-z]/, 'Must start with a lowercase letter')
        .regex(
          /^[a-z][a-z0-9_]*$/,
          'Only lowercase letters, numbers, and underscores'
        ),
      hint: z.string(),
      aiDescription: z.string(),
      requiredOption: z.enum(['optional', 'required']),
      visibilityOption: z.enum(['visible', 'visible-for-role', 'hidden']),
      visibleForRoleIds: z.array(z.string()),
      editableOption: z.enum(['editable', 'editable-for-role', 'disabled']),
      editableForRoleIds: z.array(z.string()),
      numberOfDecimals: z.number(),
      dropdownOptions: z.string()
    })
    .superRefine((value, ctx) => {
      // `dropdownOptions` is only rendered (and only relevant) when the
      // selected input type is `dropdown`; `inputType` lives outside the
      // form values, so guard presence here rather than on the field schema.
      if (
        inputType.value === 'dropdown' &&
        value.dropdownOptions.trim().length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dropdownOptions'],
          message: 'At least one option is required'
        })
      }
    })

  type AddFieldValues = z.infer<typeof addFieldSchema>

  const form = useForm({
    defaultValues: {
      label: '',
      name: '',
      hint: '',
      aiDescription: '',
      requiredOption: 'optional',
      visibilityOption: 'visible',
      visibleForRoleIds: [],
      editableOption: 'editable',
      editableForRoleIds: [],
      numberOfDecimals: 2,
      dropdownOptions: 'Option 1'
    } as AddFieldValues,
    validators: {
      onChange: addFieldSchema
    },
    onSubmit: async ({ value }) => {
      const parsedOptions = value.dropdownOptions
        .split('\n')
        .map((o) => o.trim())
        .filter(Boolean)

      const payload: AddCustomFieldRequestPayload = {
        id: globalUtils.functions.uuid(),
        name: value.name,
        label: value.label.trim(),
        entityTarget: props.target,
        inputType: inputType.value,
        hint: value.hint || null,
        aiDescription: value.aiDescription || null,
        requiredOption: value.requiredOption,
        visibilityOption: value.visibilityOption,
        visibleForRoleIds:
          value.visibilityOption === 'visible-for-role'
            ? value.visibleForRoleIds
            : null,
        editableOption: value.editableOption,
        editableForRoleIds:
          value.editableOption === 'editable-for-role'
            ? value.editableForRoleIds
            : null,
        numberOfDecimals:
          inputType.value === 'number' ? value.numberOfDecimals : null,
        dropdownOptions: inputType.value === 'dropdown' ? parsedOptions : null
      }

      emit('add', payload)
      open.value = false
    }
  })

  const formValues = form.useStore((s) => s.values)

  const nameManuallyEdited = ref(false)
  let lastAutoGeneratedName = ''

  watch(
    () => formValues.value.label,
    (label) => {
      if (!nameManuallyEdited.value && typeof label === 'string' && label) {
        const generated = generateFieldName(label)
        lastAutoGeneratedName = generated
        form.setFieldValue('name', generated)
      }
    }
  )

  watch(
    () => formValues.value.name,
    (name) => {
      if (name !== lastAutoGeneratedName) {
        nameManuallyEdited.value = true
      }
    }
  )

  // ── Input type options ────────────────────────────────────────

  interface InputTypeOption {
    type: FieldInputType
    title: string
    description: string
    icon: Component
  }

  const inputTypeOptions: InputTypeOption[] = [
    {
      type: 'text',
      title: 'Text',
      description: 'Single line text input',
      icon: IconTextCursor
    },
    {
      type: 'multiline',
      title: 'Multiline',
      description: 'Multi-line text area',
      icon: IconAlignLeft
    },
    {
      type: 'number',
      title: 'Number',
      description: 'Numeric input with decimals',
      icon: IconHash
    },
    {
      type: 'datetime',
      title: 'Date & Time',
      description: 'Date and time picker',
      icon: IconCalendarDays
    },
    {
      type: 'checkbox',
      title: 'Checkbox',
      description: 'True / false toggle',
      icon: IconSquareCheck
    },
    {
      type: 'dropdown',
      title: 'Dropdown',
      description: 'Select from a list of options',
      icon: IconChevronDown
    }
  ]

  // ── Computed ──────────────────────────────────────────────────

  const dialogTitle = computed(() =>
    step.value === 1
      ? 'Choose Field Type'
      : `New ${inputTypeOptions.find((o) => o.type === inputType.value)?.title} Field`
  )

  const dialogDescription = computed(() =>
    step.value === 1
      ? 'Select the type of input for your custom field.'
      : 'Configure the properties for your new field.'
  )

  // ── Actions ───────────────────────────────────────────────────

  function selectType(type: FieldInputType) {
    inputType.value = type
    step.value = 2
  }

  function goBack() {
    step.value = 1
  }

  function reset() {
    step.value = 1
    inputType.value = 'text'
    nameManuallyEdited.value = false
    form.reset()
  }

  function showModal(payload: AddCustomFieldRequestPayload) {
    inputType.value = payload.inputType
    step.value = 2
    form.setFieldValue('label', payload.label)
    form.setFieldValue('name', payload.name)
    nameManuallyEdited.value = true
    form.setFieldValue('hint', payload.hint ?? '')
    form.setFieldValue('aiDescription', payload.aiDescription ?? '')
    form.setFieldValue('requiredOption', payload.requiredOption)
    form.setFieldValue('visibilityOption', payload.visibilityOption)
    form.setFieldValue('visibleForRoleIds', payload.visibleForRoleIds ?? [])
    form.setFieldValue('editableOption', payload.editableOption)
    form.setFieldValue('editableForRoleIds', payload.editableForRoleIds ?? [])
    form.setFieldValue('numberOfDecimals', payload.numberOfDecimals ?? 2)
    form.setFieldValue(
      'dropdownOptions',
      payload.dropdownOptions?.join('\n') ?? ''
    )
    open.value = true
  }

  defineExpose({ showModal })

  // Reset when dialog closes
  watch(open, (val) => {
    if (!val) reset()
  })
</script>

<template>
  <modal v-model:open="open" type="dialog">
    <ModalHeader>
      <ModalHeaderTitle>{{ dialogTitle }}</ModalHeaderTitle>
      <ModalHeaderDescription>{{ dialogDescription }}</ModalHeaderDescription>
    </ModalHeader>
    <ModalSection>
      <!-- Step 1: Pick input type -->
      <ModalContent v-if="step === 1" class="grid grid-cols-2 gap-2">
        <button
          v-for="option in inputTypeOptions"
          :key="option.type"
          class="border-border hover:border-primary hover:bg-primary/5 flex items-start gap-3 rounded-lg border p-3 text-left transition-colors"
          @click="selectType(option.type)"
        >
          <component
            :is="option.icon"
            class="text-muted-foreground mt-0.5 h-5 w-5 shrink-0"
          />
          <div>
            <div class="text-sm font-medium">{{ option.title }}</div>
            <div class="text-muted-foreground text-xs">
              {{ option.description }}
            </div>
          </div>
        </button>
      </ModalContent>

      <!-- Step 2: Properties -->
      <ModalContent v-else class="flex flex-col gap-4">
        <FieldGroup>
          <FormField
            :form="form"
            name="label"
            label="Label"
            hint="The label on the field shown to the end user."
            required="Label is required"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="name"
            label="Name"
            hint="Unique identifier for this field. Auto-generated from label. Used for integrations and document codes."
            required="Name is required"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="hint"
            label="Hint"
            hint="A tooltip hint shown to the end user - just like this one!"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="aiDescription"
            label="AI Description"
            hint="Instructions for AI on how to extract this field's value."
          >
            <template #default="{ field }">
              <InputGroupTextarea
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :rows="2"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
        <FieldGroup v-if="inputType === 'number'">
          <FormField
            :form="form"
            required
            name="numberOfDecimals"
            label="Number of Decimals"
          >
            <template #default="{ field }">
              <InputGroupNumber
                :id="field.name"
                :model-value="field.state.value"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
        <FieldGroup v-if="inputType === 'dropdown'">
          <FormField
            :form="form"
            required
            name="dropdownOptions"
            label="Dropdown Options"
            hint="One option per line."
          >
            <template #default="{ field }">
              <InputGroupTextarea
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :rows="4"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
        <h3 class="text-sm font-medium">Field Options</h3>
        <FieldValidationConfigActionCard
          v-if="inputType !== 'checkbox'"
          :form="form"
        />
        <FieldVisibilityConfigActionCard :form="form" />
        <FieldEditableConfigActionCard :form="form" />
      </ModalContent>
    </ModalSection>
    <ModalFooter>
      <template v-if="step === 1">
        <Button class="ml-auto" variant="ghost" size="lg" @click="open = false">
          Cancel
        </Button>
      </template>
      <template v-else>
        <Button class="ml-auto" variant="ghost" size="lg" @click="goBack">
          Back
        </Button>
        <FormSubmit :form="form" label="Add Field" :require-dirty="false" />
      </template>
    </ModalFooter>
  </modal>
</template>
